import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import dynamic from "next/dynamic";
import TiptapEditor from "@/components/tiptap/editor";
import Badge from "@/components/ui/badge";
import { getFileIcon } from "@/lib/file-utils";
import { formatDate, formatToHumanReadable, splitDateAnswer } from "@/lib/format-data";
import { formatOptionValue, optionDisplay, optionLabelsByValue } from "@/lib/forms/option-display";
import { isIcd10CodesQuestion, readIcd10Answer } from "@/lib/icd10-codes-field";
import { looksLikeRichHtml } from "@/lib/note-utils";
import { isPatientReportedMedicationQuestion, splitPatientReportedMedicationValue } from "@/lib/patient-reported-medications";
import { useStoreTimezone } from "@/lib/timezone";
import { DocumentSummary } from "@/types/document";
import { DentalChartAnswer, DentalChart as DentalChartConfig, Disclaimer, OcclusionExamAnswer, OcclusionExam as OcclusionExamConfig, PerioChartAnswer, PerioChart as PerioChartConfig, QuestionType, Statement, SuppliesUsed } from "@/types/forms/question";
import { NoteAnswerDetail, NoteQuestionDetail } from "@/types/notes";
import { AttachmentsGrid } from "./attachments-grid";
import { Icd10CodesList } from "./icd10-codes-answer";
import { MedicationAnswerEditor } from "./medication-answer-editor";
import { RebookingAnswerEditor } from "./rebooking-answer-editor";
import { SoldItemsAnswerEditor } from "./sold-items-answer-editor";
import { SuppliesUsedAnswerEditor } from "./supplies-used-answer-editor";
import { TableAnswerEditor } from "./table-answer-editor";
import { WeightAnswerRenderer } from "./weight-answer-renderer";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
const DentalChartLoading = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-32 animate-pulse rounded-lg bg-background-secondary/30" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const DentalChartComponent = dynamic(() => import("@/components/dental/dental-chart"), {
  ssr: false,
  loading: DentalChartLoading
});
const OcclusionExamComponent = dynamic(() => import("@/components/dental/occlusion-exam"), {
  ssr: false,
  loading: DentalChartLoading
});
const PerioChartComponent = dynamic(() => import("@/components/dental/perio-chart"), {
  ssr: false,
  loading: DentalChartLoading
});

/**
 * How a saved select answer reads back, with the clinic's wording where there is any.
 *
 * The answer stores the raw option, so the label is looked up on the question it was
 * answered from. A question with no labels reads exactly as it did before.
 */
const showSelectOption = (question: NoteQuestionDetail, value: string): string => {
  const {
    options,
    optionLabels
  } = (question.data ?? {}) as {
    options?: string[];
    optionLabels?: string[];
  };
  return optionDisplay(formatOptionValue(value), optionLabelsByValue(options, optionLabels)?.[value]);
};
interface NoteAnswerRendererProps {
  answer: NoteAnswerDetail;
  question: NoteQuestionDetail;
  noteId?: string;
  patientId?: string;
  viewer?: "provider" | "patient";
}
export const NoteAnswerRenderer: React.FC<NoteAnswerRendererProps> = ({
  answer,
  question,
  noteId,
  patientId,
  viewer = "provider"
}) => {
  const {
    data
  } = answer;
  const timeZone = useStoreTimezone();

  // MEDICATIONS question type gets special treatment
  if (question.questionType === QuestionType.MEDICATIONS) {
    if (viewer === "patient") {
      return <MedicationAnswerReadOnly answer={answer} />;
    }
    // For medications, show the medication editor in read-only mode
    return <MedicationAnswerEditor answer={answer} onChange={() => {}} // No-op for renderer
    disabled={true} noteId={noteId} patientId={patientId} />;
  }

  // SOLD_ITEMS: show the picker in read-only mode so saved items are visible.
  if (question.questionType === QuestionType.SOLD_ITEMS) {
    return <SoldItemsAnswerEditor answer={answer} onChange={() => {}} disabled={true} />;
  }
  if (question.questionType === QuestionType.SUPPLIES_USED) {
    return <SuppliesUsedAnswerEditor answer={answer} onChange={() => {}} disabled={true} noteId={noteId} questionData={question.data as SuppliesUsed} />;
  }

  // REBOOKING: show the picker read-only so saved follow-ups stay visible.
  if (question.questionType === QuestionType.REBOOKING) {
    return <RebookingAnswerEditor answer={answer} onChange={() => {}} disabled={true}
    // The due day comes from the provider note query, which the patient
    // portal can't read.
    noteId={viewer === "provider" ? noteId : undefined} />;
  }

  // TABLE: render the grid read-only.
  if (question.questionType === QuestionType.TABLE) {
    return <TableAnswerEditor answer={answer} question={question} onChange={() => {}} disabled={true} />;
  }
  if (isIcd10CodesQuestion(question)) {
    return <Icd10CodesList {...readIcd10Answer(data)} />;
  }
  if (isPatientReportedMedicationQuestion(question)) {
    const medications = splitPatientReportedMedicationValue(data.values ?? data.value);
    if (medications.length > 0) {
      return <div className="flex flex-wrap gap-2">
          {medications.map((medication, index) => <Badge key={index} variant="default">
              {medication}
            </Badge>)}
        </div>;
    }
    return <div className="text-body text-foreground-muted">—</div>;
  }
  switch (question.questionType) {
    case QuestionType.TEXT:
    case QuestionType.TEXT_AREA:
      {
        const textValue = data.value !== null && data.value !== undefined ? String(data.value) : "";
        if (!textValue) {
          return <div className="text-body text-foreground">—</div>;
        }
        // Legacy plain-text answers render verbatim — feeding them through the
        // editor's HTML parser would silently drop tag-like substrings (e.g.
        // "I feel <great>") and decode entities. Only values our serializer
        // produced (wrapped in <p>/<strong>/…) go through the read-only editor,
        // which re-parses them through its schema so only p/strong/em/u/br survive.
        if (!looksLikeRichHtml(textValue)) {
          return <div className="whitespace-pre-wrap text-body text-foreground">
            {textValue}
          </div>;
        }
        return <TiptapEditor content={textValue} richText editable={false} suggestions={[]} textArea={false} editorClassName="!min-h-0 !rounded-none !border-0 !px-0 !py-0 whitespace-pre-wrap text-body text-foreground" />;
      }
    case QuestionType.NUMBER:
      return <div className="text-body text-foreground">
          {data.value !== null && data.value !== undefined ? String(data.value) : "—"}
        </div>;
    case QuestionType.POUNDS_WEIGHT:
      return <WeightAnswerRenderer answer={answer} noteId={noteId} patientId={patientId} />;
    case QuestionType.FEET_INCHES_HEIGHT:
      {
        // Handle both old format {feet: number, inches: number} and new format {value: {feet: number, inches: number}}
        let heightData: {
          feet: number;
          inches: number;
        } | null = null;
        if (typeof data === "object" && data !== null) {
          if ("value" in data && typeof data.value === "object" && data.value !== null) {
            // New format: {value: {feet: number, inches: number}}
            const value_0 = data.value as {
              feet?: number;
              inches?: number;
            };
            if ("feet" in value_0 && "inches" in value_0 && value_0.feet !== undefined && value_0.feet !== null && value_0.inches !== undefined && value_0.inches !== null) {
              heightData = {
                feet: value_0.feet,
                inches: value_0.inches
              };
            }
          } else if ("feet" in data && "inches" in data) {
            // Old format: {feet: number, inches: number}
            heightData = {
              feet: data.feet as number,
              inches: data.inches as number
            };
          }
        }
        if (heightData) {
          return <div className="text-body text-foreground">
            {heightData.feet}&apos; {heightData.inches}&quot;
          </div>;
        }
        return <div className="text-body text-foreground">
          {data.value !== null && data.value !== undefined ? String(data.value) : "—"}
        </div>;
      }
    case QuestionType.DATE:
      if (typeof data.value === "string") {
        try {
          // A question with `includeTime` stores a UTC instant; splitDateAnswer
          // resolves it to the clinic's date and time, and formatDate then
          // formats a plain calendar string it cannot shift again.
          const {
            date: datePart,
            time: timePart
          } = splitDateAnswer(data.value, timeZone);
          return <div className="text-body text-foreground">
              <span>{formatDate(datePart)}</span>
              {timePart ? <span>{` at ${timePart}`}</span> : null}
            </div>;
        } catch {
          return <div className="text-body text-foreground">{data.value}</div>;
        }
      }
      return <div className="text-body text-foreground">—</div>;
    case QuestionType.SINGLE_SELECT:
    case QuestionType.RADIAL_SINGLE_SELECT:
      if (data.value) {
        return <Badge variant="default">
            {showSelectOption(question, data.value)}
          </Badge>;
      }
      return <div className="text-body text-foreground-muted">—</div>;
    case QuestionType.MULTI_SELECT:
    case QuestionType.RADIAL_MULTI_SELECT:
    case QuestionType.TAG:
      // TAG questions (e.g., allergies) use "values" format like MULTI_SELECT
      if (data.values && Array.isArray(data.values) && data.values.length > 0) {
        return <div className="flex flex-wrap gap-2">
            {data.values.map((value: string, index_1: number) => <Badge key={index_1} variant="default">
                {showSelectOption(question, value)}
              </Badge>)}
          </div>;
      }
      return <div className="text-body text-foreground-muted">—</div>;
    case QuestionType.TOGGLE:
      {
        let toggleLabel = "—";
        if (data.value === true) toggleLabel = "Yes";else if (data.value === false) toggleLabel = "No";
        return <div className="text-body text-foreground">{toggleLabel}</div>;
      }
    case QuestionType.FILE_UPLOAD:
      if (viewer === "patient") {
        return <div className="text-body text-foreground-muted">
            Attachment shared
          </div>;
      }
      return <ScribeDocumentsRenderer noteId={noteId} fallbackAttachmentId={data.attachment_id} />;
    case QuestionType.SCRIBE_ATTACHMENTS:
      if (viewer === "patient") {
        return <div className="text-body text-foreground-muted">
            Attachment shared
          </div>;
      }
      return <ScribeAttachmentsRenderer noteId={noteId} patientId={patientId} />;
    case QuestionType.DENTAL_CHART:
      {
        if (viewer === "patient") {
          return <div className="text-body text-foreground-muted">
            Clinical chart — provider only
          </div>;
        }
        const cfg_1 = question.data as unknown as DentalChartConfig;
        const current_1: DentalChartAnswer = data?.value as DentalChartAnswer ?? {
          teeth: {}
        };
        return <DentalChartComponent questionData={cfg_1} value={current_1} onChange={() => {}} disabled />;
      }
    case QuestionType.PERIO_CHART:
      {
        if (viewer === "patient") {
          return <div className="text-body text-foreground-muted">
            Clinical chart — provider only
          </div>;
        }
        const cfg_0 = question.data as unknown as PerioChartConfig;
        const current_0: PerioChartAnswer = data?.value as PerioChartAnswer ?? {
          teeth: {}
        };
        return <PerioChartComponent questionData={cfg_0} value={current_0} onChange={() => {}} disabled />;
      }
    case QuestionType.OCCLUSION_EXAM:
      {
        if (viewer === "patient") {
          return <div className="text-body text-foreground-muted">
            Clinical chart — provider only
          </div>;
        }
        const cfg = question.data as unknown as OcclusionExamConfig;
        const current: OcclusionExamAnswer = data?.value as OcclusionExamAnswer ?? {};
        return <OcclusionExamComponent questionData={cfg} value={current} onChange={() => {}} disabled />;
      }
    case QuestionType.SIGNATURE:
      if (typeof data.value === "string" && data.value) {
        return (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={data.value} alt="Signature" className="max-h-40 rounded border border-border bg-background" />
        );
      }
      return <div className="text-body text-foreground-muted">Not signed</div>;
    case QuestionType.PROVIDER_SIGNATURE:
      {
        const signatureUrl = typeof data.signatureImageUrl === "string" ? data.signatureImageUrl : null;
        const providerName = typeof data.providerName === "string" ? data.providerName : null;
        if (signatureUrl) {
          return <div className="space-y-1">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={signatureUrl} alt="Signature" className="max-h-40 rounded border border-border bg-background" />
            {providerName && <p className="text-caption text-foreground-secondary">
                {providerName}
              </p>}
          </div>;
        }
        return <div className="text-body text-foreground-muted">
          {providerName ? `${providerName} (no signature on file)` : "Not signed"}
        </div>;
      }
    case QuestionType.DISCLAIMER:
      {
        const disclaimer = question.data as unknown as Disclaimer | undefined;
        return <div className="whitespace-pre-wrap text-body text-foreground-secondary">
          {disclaimer?.content || "—"}
        </div>;
      }
    case QuestionType.STATEMENT:
      {
        const statement = question.data as unknown as Statement | undefined;
        if (statement?.points && statement.points.length > 0) {
          return <ul className="list-disc space-y-1 pl-5 text-body text-foreground-secondary">
            {statement.points.map((point, index_0) => <li key={index_0}>{point}</li>)}
          </ul>;
        }
        return <div className="text-body text-foreground-muted">—</div>;
      }
    default:
      return <div className="text-body text-foreground-muted">
          {JSON.stringify(data) || "—"}
        </div>;
  }
};
export const ScribeDocumentsRenderer: React.FC<{
  noteId?: string;
  fallbackAttachmentId?: string;
}> = t0 => {
  const $ = _c(14);
  const {
    noteId,
    fallbackAttachmentId
  } = t0;
  let t1;
  if ($[0] !== noteId) {
    t1 = noteQueryOptions.noteDocuments(noteId ?? "");
    $[0] = noteId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !!noteId;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: t4,
    isLoading
  } = useQuery(t3);
  let t5;
  if ($[5] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const documents = t5;
  if (!noteId) {
    let t6;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="text-body text-foreground-muted">—</div>;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    return t6;
  }
  if (!isLoading && documents.length === 0) {
    if (fallbackAttachmentId) {
      let t6;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <div className="text-body text-foreground-muted">File attached</div>;
        $[8] = t6;
      } else {
        t6 = $[8];
      }
      return t6;
    }
    let t6;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="text-body text-foreground-muted">—</div>;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  let t6;
  if ($[10] !== documents) {
    t6 = documents.map(_temp11);
    $[10] = documents;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <div className="space-y-2">{t6}</div>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};
interface MedicationAnswerEntry {
  name?: string;
  amount?: number | null;
  unit?: string | null;
  frequency?: string | null;
  quantity?: number | null;
  administrationLocation?: string | null;
  comment?: string | null;
}
const MedicationAnswerReadOnly: React.FC<{
  answer: NoteAnswerDetail;
}> = t0 => {
  const $ = _c(7);
  const {
    answer
  } = t0;
  let t1;
  if ($[0] !== answer.data) {
    t1 = Array.isArray((answer.data as {
      medications?: unknown;
    }).medications) ? (answer.data as {
      medications: MedicationAnswerEntry[];
    }).medications ?? [] : [];
    $[0] = answer.data;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const meds = t1;
  if (meds.length === 0) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="text-body text-foreground-muted">—</div>;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t2;
  if ($[3] !== meds) {
    t2 = meds.map(_temp12);
    $[3] = meds;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <div className="space-y-1.5">{t2}</div>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export const ScribeAttachmentsRenderer: React.FC<{
  noteId?: string;
  patientId?: string;
}> = t0 => {
  const $ = _c(16);
  const {
    noteId,
    patientId
  } = t0;
  let t1;
  if ($[0] !== noteId) {
    t1 = noteQueryOptions.attachments(noteId ?? "");
    $[0] = noteId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !!noteId;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: t4,
    isLoading
  } = useQuery(t3);
  let t5;
  if ($[5] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const attachments = t5;
  if (!noteId) {
    let t6;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="text-body text-foreground-muted">—</div>;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    return t6;
  }
  if (!isLoading && attachments.length === 0) {
    let t6;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="text-body text-foreground-muted">—</div>;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  let t6;
  if ($[9] !== attachments) {
    t6 = attachments.map(_temp13);
    $[9] = attachments;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const gridAttachments = t6;
  let t7;
  if ($[11] !== gridAttachments || $[12] !== isLoading || $[13] !== noteId || $[14] !== patientId) {
    t7 = <AttachmentsGrid attachments={gridAttachments} isLoading={isLoading} size="lg" groupByDate={false} showUpload={false} patientId={patientId} noteId={noteId} showNoteLink={false} />;
    $[11] = gridAttachments;
    $[12] = isLoading;
    $[13] = noteId;
    $[14] = patientId;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
function _temp11(doc) {
  return <div key={doc.id} className="flex items-center space-x-3 rounded-md border border-border px-3 py-2"><div className="flex-shrink-0">{getFileIcon(doc.fileType)}</div><div className="min-w-0 flex-1"><p className="truncate text-body-strong text-foreground">{doc.title}</p><p className="text-caption text-foreground-muted">{formatToHumanReadable(doc.docType)}</p></div></div>;
}
function _temp12(med, idx) {
  const dose = [med.amount, med.unit].filter(Boolean).join(" ");
  const detail = [dose, med.frequency, med.administrationLocation].filter(Boolean).join(" \xB7 ");
  return <div key={idx} className="text-body text-foreground"><span className="font-medium">{med.name || "Medication"}</span>{detail && <span className="ml-2 text-foreground-secondary">{detail}</span>}</div>;
}
function _temp13(a) {
  return {
    id: a.id,
    attachmentId: a.id,
    url: a.signedUrl || "",
    signedUrl: a.signedUrl,
    thumbnailSignedUrl: a.thumbnailSignedUrl,
    gcsPath: a.gcsPath,
    annotations: a.annotations,
    name: a.name,
    date: a.createdDate,
    uploadedAt: a.uploadedAt,
    tags: a.tags,
    note: a.note
  };
}